import { useEffect, useState } from "react";
import { useClient } from "@/utils/client/react";
import WeekView from "@components/WeekView";
import SchedulerHeader from "@components/SchedulerHeader";
import TimeSlots from "@components/TimeSlots";
import { startOfWeek, endOfDay, addDays } from "date-fns";
import ConfirmationForm from "@components/ConfirmationForm";
import SchedulerConfirmation from "@components/SchedulerConfirmation";
import { Grid } from "react-loader-spinner";
import styled from "styled-components";
import { SchedulerGem } from "@/gem.types";
import { Contact } from "@/types";
import type { PageData } from "@/gem.types";
import type { AvailableTimeSlots } from "@/types";

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  position: absolute;
`;

const getDurationLabel = (duration: number) => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  const hourLabel = hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const minuteLabel =
    minutes > 0 ? `${minutes} minute${minutes > 1 ? "s" : ""}` : "";

  if (hours > 0 && minutes > 0) {
    return `${hourLabel} and ${minuteLabel}`;
  } else if (hours > 0) {
    return hourLabel;
  } else {
    return minuteLabel;
  }
};

interface Props {
  gem: SchedulerGem;
  contact?: Contact;
  inPage?: boolean;
  theme?: PageData["theme"];
  apis: Record<string, string>;
}

const Scheduler = ({ gem, contact, inPage, theme, apis }: Props) => {
  const client = useClient(apis.core);
  const [timezone, setTimezone] = useState<string>("America/Chicago");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [view, setView] = useState<
    "slots" | "form" | "confirmation" | "loading"
  >("slots");
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [availableTimes, setAvailableTimes] = useState<AvailableTimeSlots>({
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
  });
  const [weekStart, setWeekStart] = useState<Date>(new Date());
  const [weekEnd, setWeekEnd] = useState<Date>(new Date());
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const startOfTheWeek = startOfWeek(new Date(), { weekStartsOn: 0 });
    const endOfTheWeek = endOfDay(addDays(startOfTheWeek, 6));

    setWeekStart(startOfTheWeek);
    setWeekEnd(endOfTheWeek);
    setTimezone(userTimezone);
  }, []);

  const handleWeekChange = async (startOfWeek: Date, endOfWeek: Date) => {
    setWeekStart(startOfWeek);
    setWeekEnd(endOfWeek);
  };

  const getDayOfWeek = (date: Date): keyof AvailableTimeSlots => {
    const days: (keyof AvailableTimeSlots)[] = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return days[date.getDay()];
  };

  const selectedDay = getDayOfWeek(selectedDate);
  const selectedDayTimeslots = availableTimes[selectedDay] || [];

  useEffect(() => {
    let mounted = true;

    async function fetchSchedule() {
      setLoading(true);
      try {
        const res = await client.public.scheduler.$get({
          query: {
            gemId: gem.gemId,
            userId: gem.userId,
            timezone,
            weekStart: weekStart.toISOString(),
            weekEnd: weekEnd.toISOString(),
          },
        });

        if (mounted) {
          const data = await res.json();
          if (!data) return;
          setAvailableTimes(data);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching schedule:", error);
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchSchedule();
    return () => {
      mounted = false;
    };
  }, [gem.gemId, timezone, weekStart, weekEnd, client]);

  if (!gem) {
    return null;
  }

  const onConfirmation = async (data: any) => {
    try {
      setView("loading");
      const response = await client.public.scheduler.confirm.$post({
        json: {
          userId: gem.userId,
          gemId: gem.gemId,
          contactId: contact?.contactId,
          date: selectedDate.toISOString(),
          time: selectedSlot!,
          duration: gem.duration,
          title: `${gem.name} (${data.firstName} ${data.lastName})`,
          timezone,
          meetingType: gem.eventType,
          ...data,
        },
      });

      if (response.ok) {
        setView("confirmation");
      }
    } catch (error) {
      console.error("Error confirming booking:", error);
      alert("Error confirming booking please try again later");
      setView("slots");
    }
  };

  const renderView = () => {
    if (view === "confirmation") {
      return (
        <>
          <SchedulerHeader
            name={gem.name}
            duration={getDurationLabel(gem.duration)}
            description={`${selectedSlot} on ${selectedDate.toLocaleDateString(
              "en-US",
              {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              },
            )}`}
            theme={theme}
          />
          <SchedulerConfirmation />
        </>
      );
    }

    if (view === "loading") {
      if (inPage) {
        return <Grid color={theme?.primaryColor} height={60} width={60} />;
      }
      return <LoaderContainer>Loading...</LoaderContainer>;
    }

    if (view === "form") {
      return (
        <>
          <SchedulerHeader
            name={gem.name}
            theme={theme}
            duration={getDurationLabel(gem.duration)}
            description={`You have selected ${selectedSlot} on ${selectedDate.toLocaleDateString(
              "en-US",
              {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              },
            )}`}
            buttonText="Choose a different time"
            buttonAction={() => setView("slots")}
          />
          <ConfirmationForm theme={theme} onConfirmation={onConfirmation} />
        </>
      );
    }
    return (
      <>
        <SchedulerHeader
          name={gem.name}
          theme={theme}
          duration={getDurationLabel(gem.duration)}
          description={gem.description}
        />
        <WeekView
          onWeekChange={handleWeekChange}
          selectedDay={selectedDate}
          setSelectedDay={setSelectedDate}
          timeslots={availableTimes}
          theme={theme}
        />
        <TimeSlots
          onSelect={(time) => {
            setSelectedSlot(time);
            setView("form");
          }}
          timezone={timezone}
          onTimezoneChange={setTimezone}
          loading={loading}
          selectedDate={selectedDate}
          timeslots={selectedDayTimeslots}
          theme={theme}
        />
      </>
    );
  };

  if (inPage) {
    return renderView();
  }

  return (
    <div className="w-full min-h-screen flex justify-center">
      <div className="max-w-lg h-screen overflow-y-auto w-full py-12 flex flex-col items-center justify-start px-6 transition-all duration-300 ease-in-out md:h-auto md:my-6 md:shadow-md md:rounded-lg">
        {renderView()}
      </div>
    </div>
  );
};

export default Scheduler;
